import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {GeoLocationDialogsFactory} from "./geo-location-dialogs-factory";
import {IGeoRunResult} from "../strategies/geo-location.strategy.interface";
import {
    GeoRunnerAction,
    GeoRunnerName,
    IGeoLocationRunner,
    ILoginGeoLocationOptions,
    IPeriodicGeoUpdateOptions,
    IGeoRetryOnOpenRealGame, IOpenGameGeoLocationOptions
} from "./geo-location-runner.interface";
import {IGeoRestrictionsUpdateEventParams} from "../../sdk/sdk-events";
import {GeoAnalyticsContext, GeoLocationAnalytics} from "../geo-location-analytics";
import {NullableUndefinedString} from "../../../../common/types/nullable-types";
import {LocalStorageKeys} from "../../storage/local-storage-keys";
import {Check} from "@sparkware/app-utils";
import {formatErrorForLogging} from "../geo-helpers";

export abstract class GeoLocationRunnerBase implements IGeoLocationRunner{
    constructor(protected readonly services: IServiceFactoryExtended, analyticsContext: GeoAnalyticsContext) {
        this._analytics = new GeoLocationAnalytics(analyticsContext, this._getRunnerName(), services);
        this._geoDialogs = new GeoLocationDialogsFactory(services, this._analytics);

    }

    private _runGeoPromise: Promise<IGeoRunResult>;
    protected readonly _geoDialogs: GeoLocationDialogsFactory;
    protected readonly _analytics: GeoLocationAnalytics;

    protected abstract _getRunnerName(): GeoRunnerName;

    abstract geoKick(params: IGeoRestrictionsUpdateEventParams): Promise<IGeoRunResult>;
    abstract loginGeoLocation(options: ILoginGeoLocationOptions): Promise<IGeoRunResult>;
    abstract openGameGeoLocation(options: IOpenGameGeoLocationOptions): Promise<IGeoRunResult>;
    abstract runPeriodicGeo(options: IPeriodicGeoUpdateOptions): Promise<IGeoRunResult>;
    abstract showGameRestrictedPopup(geoResult?: IGeoRunResult | null): void;
    abstract geoRetryOnOpenRealGame(options: IGeoRetryOnOpenRealGame): Promise<IGeoRunResult>;
    abstract showGenericGeoKickMessage(): Promise<void>;

    public waitForGeo(): Promise<IGeoRunResult> {
        if(!this._runGeoPromise) {
            return Promise.reject(`${this._getRunnerName()}.waitForGeo called before performGeoLocation`);
        }

        return this._runGeoPromise;
    }


    protected async _executeGeoFlow(actionName: GeoRunnerAction, geoAction: () => Promise<IGeoRunResult>): Promise<IGeoRunResult> {
        if(this._runGeoPromise) {
            return Promise.reject(`Cannot execute ${this._getRunnerName()}.${actionName} because another geo is already in progress`);
        }

        this._runGeoPromise = geoAction().then(result => {
            this._analytics.reportGeoResult(result);

            return result;
        }).catch(err => {
            this._analytics.reportGeoFailure(err);
            throw err;
        });

        return this._runGeoPromise;
    }

    private get storedClientIpAddress(): NullableUndefinedString {
        return this.services.localStorage.getItem(LocalStorageKeys.clientIpAddress);
    }

    private set storedClientIpAddress(value: NullableUndefinedString) {
        if(Check.isEmpty(value)) {
            this.services.localStorage.removeItem(LocalStorageKeys.clientIpAddress);
        } else {
            this.services.localStorage.setItem(LocalStorageKeys.clientIpAddress, value);
        }
    }

    async runClientIpDetection(): Promise<void> {
        try {

            const previousIpAddress = this.storedClientIpAddress ?? "";
            const response = await this.services.api.locationController.detectUserIp({
                PreviousIpAddress: previousIpAddress
            });

            if(!Check.isEmpty(previousIpAddress) && response.CurrentIpAddress !== previousIpAddress) {
                this._analytics.reportIpChangeDetection(previousIpAddress, response.CurrentIpAddress);
            }

            this.storedClientIpAddress = response.CurrentIpAddress;

        } catch (err) {
            this._analytics.reportIpDetectionFailure(formatErrorForLogging(err));
        }
    }
}